'use client';

import { useCallback } from 'react';
import { Node, Edge, MarkerType } from 'reactflow';
import { GraphNode, GraphEdge, EntityType } from '@/types/graph';

export type LayoutType = 'hierarchical' | 'circular' | 'force-directed' | 'radial';

export const nodeColors: Record<EntityType, { bg: string; border: string; text: string }> = {
  person: { bg: '#E8F4F8', border: '#028297', text: '#003D47' },
  organization: { bg: '#FFF4E6', border: '#D97706', text: '#4B2C0F' },
  contract: { bg: '#F0FDF4', border: '#16A34A', text: '#14532D' },
  courtCase: { bg: '#FEF2F2', border: '#DC2626', text: '#4C1010' },
  project: { bg: '#F3E8FF', border: '#9333EA', text: '#4A1D5C' },
  location: { bg: '#ECF0FF', border: '#3B82F6', text: '#1E3A8A' },
  newsArticle: { bg: '#FFF7ED', border: '#EA580C', text: '#431407' },
  document: { bg: '#F5F3FF', border: '#7C3AED', text: '#3F0F5C' },
  auditFinding: { bg: '#FEE2E2', border: '#EF4444', text: '#5F1F1F' },
};

export const edgeColors: Record<string, string> = {
  directs: '#10B981',
  awarded: '#8B5CF6',
  located_in: '#3B82F6',
  funds: '#F59E0B',
  owns: '#10B981',
  founded_by: '#3B82F6',
  employed_by: '#8B5CF6',
  party_to: '#EF4444',
  filed_in: '#F59E0B',
  mentioned_in: '#6366F1',
  involved_in: '#06B6D4',
  audits: '#EC4899',
  default: '#555',
};

export const convertToReactFlowNodes = (nodes: GraphNode[]): Node[] => {
  return nodes.map((node, idx) => {
    const normalizedType = (node.type || 'person') as EntityType;
    const color = nodeColors[normalizedType] || nodeColors.person;
    
    return {
      id: node.id,
      data: {
        label: node.name || node.id,
        type: normalizedType,
        confidence: node.confidence,
        description: node.aliases?.[0] || '',
      },
      position: { x: 0, y: 0 },
      type: 'entity',
      style: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        width: 56,
        height: 56,
        cursor: 'pointer',
      },
    };
  });
};

export const convertToReactFlowEdges = (edges: GraphEdge[]): Edge[] => {
  // Group edges by the unordered pair so we can detect parallels
  const pairKey = (a: string, b: string) => (a < b ? `${a}||${b}` : `${b}||${a}`);
  const pairCounts = new Map<string, number>();
  const pairIndex = new Map<string, number>();

  for (const edge of edges) {
    const key = pairKey(edge.fromId, edge.toId);
    pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
  }

  return edges.map((edge) => {
    const key = pairKey(edge.fromId, edge.toId);
    const total = pairCounts.get(key) || 1;
    const idx = pairIndex.get(key) || 0;
    pairIndex.set(key, idx + 1);

    // Compute curve offset: spread parallel edges symmetrically
    // 0 for single edge, ±30, ±60, etc. for parallels
    let curveOffset = 0;
    if (total > 1) {
      const spread = 40;
      curveOffset = (idx - (total - 1) / 2) * spread;
    }

    // If this edge's direction is reversed vs the canonical pair order,
    // flip the offset so it curves the opposite side
    const isReversed = edge.fromId > edge.toId;
    if (isReversed) curveOffset = -curveOffset;

    return {
      id: `${edge.fromId}-${edge.toId}-${edge.type}`,
      source: edge.fromId,
      target: edge.toId,
      label: edge.type,
      animated: false,
      type: 'circle',
      style: {
        stroke: edgeColors[edge.type?.toLowerCase()] || '#888',
        strokeWidth: 1.5,
      },
      labelStyle: {
        fontSize: '9px',
        fill: '#666',
        fontWeight: 500,
      },
      labelBgStyle: {
        fill: 'white',
        fillOpacity: 0.85,
      },
      data: { ...edge, curveOffset },
    };
  });
};

// Hierarchical (layered) layout — pure TypeScript, no external deps
export const hierarchicalLayout = (nodes: Node[], edges: Edge[]): Node[] => {
  if (nodes.length === 0) return nodes;

  const NODE_W = 200;
  const NODE_H = 120;
  const H_GAP = 80;
  const V_GAP = 60;

  // Build adjacency & compute in-degrees
  const children: Record<string, string[]> = {};
  const inDegree: Record<string, number> = {};
  nodes.forEach((n) => { children[n.id] = []; inDegree[n.id] = 0; });
  edges.forEach((e) => {
    if (children[e.source]) children[e.source].push(e.target);
    inDegree[e.target] = (inDegree[e.target] || 0) + 1;
  });

  // Topological sort into layers (Kahn's algorithm)
  const layers: string[][] = [];
  let queue = nodes.filter((n) => (inDegree[n.id] || 0) === 0).map((n) => n.id);
  if (queue.length === 0) queue = [nodes[0].id]; // cycle fallback
  const placed = new Set<string>();

  while (queue.length > 0) {
    layers.push([...queue]);
    queue.forEach((id) => placed.add(id));
    const next: string[] = [];
    queue.forEach((id) => {
      (children[id] || []).forEach((child) => {
        if (!placed.has(child)) {
          inDegree[child]--;
          if (inDegree[child] <= 0 && !next.includes(child)) next.push(child);
        }
      });
    });
    queue = next;
  }

  // Place any remaining unvisited nodes (disconnected / cycles)
  const remaining = nodes.filter((n) => !placed.has(n.id)).map((n) => n.id);
  if (remaining.length > 0) layers.push(remaining);

  // Assign positions: layers left-to-right, nodes top-to-bottom centered
  const positions: Record<string, { x: number; y: number }> = {};
  layers.forEach((layer, li) => {
    const totalHeight = layer.length * NODE_H + (layer.length - 1) * V_GAP;
    const startY = -totalHeight / 2;
    layer.forEach((id, ni) => {
      positions[id] = {
        x: li * (NODE_W + H_GAP),
        y: startY + ni * (NODE_H + V_GAP),
      };
    });
  });

  return nodes.map((node) => ({
    ...node,
    position: positions[node.id] || { x: 0, y: 0 },
  }));
};

// Circular layout
export const circularLayout = (nodes: Node[], _edges: Edge[]) => {
  const radius = Math.max(150, nodes.length * 40);
  const centerX = 0;
  const centerY = 0;

  return nodes.map((node, idx) => {
    const angle = (idx / nodes.length) * 2 * Math.PI;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);

    return {
      ...node,
      position: { x, y },
    };
  });
};

// Force-directed layout (Fruchterman-Reingold)
export const forceDirectedLayout = (nodes: Node[], edges: Edge[]) => {
  if (nodes.length === 0) return nodes;

  const iterations = 120;
  const area = 800 * 800;
  const k = Math.sqrt(area / nodes.length) * 1.2; // ideal edge length
  const gravity = 0.05;
  
  let positions: Record<string, { x: number; y: number; vx: number; vy: number }> = {};
  
  // Initialize in a circle so they start spread out
  const initRadius = Math.max(300, nodes.length * 20);
  nodes.forEach((node, i) => {
    const angle = (i / nodes.length) * 2 * Math.PI;
    positions[node.id] = {
      x: initRadius * Math.cos(angle),
      y: initRadius * Math.sin(angle),
      vx: 0,
      vy: 0,
    };
  });

  for (let iter = 0; iter < iterations; iter++) {
    const temp = k * (1 - iter / iterations); // cooling

    // Reset
    Object.keys(positions).forEach((id) => {
      positions[id].vx = 0;
      positions[id].vy = 0;
    });

    // Repulsive forces
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = positions[nodes[i].id];
        const b = positions[nodes[j].id];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const force = (k * k) / dist;
        const fx = (force * dx) / dist;
        const fy = (force * dy) / dist;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Attractive forces (edges)
    edges.forEach((edge) => {
      const a = positions[edge.source];
      const b = positions[edge.target];
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
      const force = (dist * dist) / k;
      const fx = (force * dx) / dist;
      const fy = (force * dy) / dist;
      a.vx += fx * 0.4;
      a.vy += fy * 0.4;
      b.vx -= fx * 0.4;
      b.vy -= fy * 0.4;
    });

    // Gravity toward center
    Object.values(positions).forEach((p) => {
      p.vx -= p.x * gravity;
      p.vy -= p.y * gravity;
    });

    // Apply with temperature limiting
    Object.values(positions).forEach((p) => {
      const disp = Math.sqrt(p.vx * p.vx + p.vy * p.vy) || 0.1;
      const scale = Math.min(disp, temp) / disp;
      p.x += p.vx * scale;
      p.y += p.vy * scale;
    });
  }

  return nodes.map((node) => ({
    ...node,
    position: {
      x: positions[node.id].x,
      y: positions[node.id].y,
    },
  }));
};

// Radial layout — concentric rings around the most-connected (focal) node
export const radialLayout = (nodes: Node[], edges: Edge[], focalId?: string): Node[] => {
  if (nodes.length === 0) return nodes;

  // Find focal node: user-specified or highest degree
  const degree = new Map<string, number>();
  for (const n of nodes) degree.set(n.id, 0);
  for (const e of edges) {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  }

  const focal =
    focalId && degree.has(focalId)
      ? focalId
      : [...degree.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? nodes[0].id;

  // BFS from focal to assign ring levels
  const adj = new Map<string, Set<string>>();
  for (const n of nodes) adj.set(n.id, new Set());
  for (const e of edges) {
    adj.get(e.source)?.add(e.target);
    adj.get(e.target)?.add(e.source);
  }

  const ringOf = new Map<string, number>();
  ringOf.set(focal, 0);
  const queue = [focal];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    const currRing = ringOf.get(curr)!;
    for (const neighbor of adj.get(curr) ?? []) {
      if (!ringOf.has(neighbor)) {
        ringOf.set(neighbor, currRing + 1);
        queue.push(neighbor);
      }
    }
  }
  // Disconnected nodes go to outermost ring + 1
  const maxRing = Math.max(0, ...ringOf.values());
  for (const n of nodes) {
    if (!ringOf.has(n.id)) ringOf.set(n.id, maxRing + 1);
  }

  // Group nodes by ring
  const rings = new Map<number, string[]>();
  for (const [id, ring] of ringOf) {
    if (!rings.has(ring)) rings.set(ring, []);
    rings.get(ring)!.push(id);
  }

  const RING_GAP = 140;
  const positions = new Map<string, { x: number; y: number }>();

  for (const [ring, ids] of rings) {
    if (ring === 0) {
      positions.set(ids[0], { x: 0, y: 0 });
      continue;
    }
    const radius = ring * RING_GAP;
    ids.forEach((id, i) => {
      const angle = (i / ids.length) * 2 * Math.PI - Math.PI / 2;
      positions.set(id, {
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      });
    });
  }

  return nodes.map((node) => ({
    ...node,
    position: positions.get(node.id) ?? { x: 0, y: 0 },
  }));
};

export const applyLayout = (layoutType: LayoutType, nodes: Node[], edges: Edge[]): Node[] => {
  switch (layoutType) {
    case 'circular':
      return circularLayout(nodes, edges);
    case 'force-directed':
      return forceDirectedLayout(nodes, edges);
    case 'radial':
      return radialLayout(nodes, edges);
    case 'hierarchical':
    default:
      return hierarchicalLayout(nodes, edges);
  }
};
